import React, { useState } from 'react'
import Button from '../../../ui/Button/Button'
import Icon from '../../../ui/Icon/Icon'
import { formatDateAsDayMontYearHHMM } from '../../../utils/date/dateFormat'
import copyToClipboard from '../../../utils/copyToClipboard'
import downloadFileFromUrl from '../../../utils/downloadFileFromUrl'
import Spinner from '../../../ui/Spinner/Spinner'
import FeedCopyRow from './FeedCopyRow'
import { useCatalogXlsFeed } from './useCatalogXlsFeed'

const COPY_FEEDBACK_MS = 2000

function XLSFeedCatalogPanel({ catalog, brandId }) {
  void brandId

  const [copiedId, setCopiedId] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)
  const [downloadMessage, setDownloadMessage] = useState(null)

  const {
    runtime,
    lastGeneratedAt,
    isGenerating,
    errorMessage,
    rowsCount,
    counts,
    generateFacebookFeed,
    getPlatformDownloadUrl,
    getPlatformFeedUrl,
  } = useCatalogXlsFeed(catalog)

  if (!runtime) {
    return null
  }

  const waitingForFirstFeed = catalog === 'combined' && !lastGeneratedAt

  const handleGenerate = () => {
    generateFacebookFeed()
  }

  const handleCopy = async (copyId, text) => {
    if (!text) {
      return
    }

    try {
      await copyToClipboard(text)
      setCopiedId(copyId)
      window.setTimeout(() => {
        setCopiedId((current) => (current === copyId ? null : current))
      }, COPY_FEEDBACK_MS)
    } catch (error) {
      console.error('Copy to clipboard failed:', error)
    }
  }

  const handlePlatformClick = async (platform) => {
    setDownloadMessage(null)

    const downloadUrl = getPlatformDownloadUrl(platform)

    if (!downloadUrl) {
      setDownloadMessage(
        `Фід ${platform.feedLabel} ще недоступний. Спочатку натисніть «Згенерувати».`
      )
      return
    }

    setDownloadingId(platform.id)

    try {
      await downloadFileFromUrl({
        url: downloadUrl,
        fileName: platform.downloadFileName,
      })
    } catch (error) {
      console.error('Feed download failed:', error)
      const isNotFound = /404/.test(error?.message || '')
      setDownloadMessage(
        isNotFound
          ? `Файл ${platform.feedLabel} ще не створено. Натисніть «Згенерувати».`
          : error?.message || `Не вдалося завантажити файл ${platform.feedLabel}`
      )
    } finally {
      setDownloadingId(null)
    }
  }

  const lastGenLabel = lastGeneratedAt
    ? formatDateAsDayMontYearHHMM(lastGeneratedAt)
    : '—'

  const rowsCountLabel =
    typeof rowsCount === 'number' ? ` (${rowsCount} товарів)` : ''

  return (
    <div className="XLSFeedPopUp-CatalogPanel">
      <div className="XLSFeedPopUp-Toolbar">
        <Button
          type="button"
          theme="solid"
          fill="accent"
          title={catalog === 'combined' ? 'Оновити загальний фід' : 'Оновити XLS на сервері'}
          isLoading={isGenerating}
          onClick={handleGenerate}
        />
        <p className="XLSFeedPopUp-LastGen">
          Остання генерація: <span>{lastGenLabel}</span>
          {counts ? ` · Premium: ${counts.premium} · Outlet: ${counts.outlet}` : rowsCountLabel}
        </p>
      </div>

      {errorMessage && <p className="XLSFeedPopUp-Error">{errorMessage}</p>}
      {downloadMessage && <p className="XLSFeedPopUp-Error">{downloadMessage}</p>}

      <div className="XLSFeedPopUp-Platforms">
        {runtime.platforms.map((platform) => {
          const isDownloading = downloadingId === platform.id

          return (
            <button
              key={platform.id}
              type="button"
              className={`XLSFeedPopUp-PlatformButton${
                isDownloading ? ' XLSFeedPopUp-PlatformButton_loading' : ''
              }`}
              title={`Завантажити актуальний XLS (${platform.label})`}
              disabled={isDownloading || waitingForFirstFeed}
              onClick={() => handlePlatformClick(platform)}
            >
              {isDownloading ? (
                <span className="XLSFeedPopUp-PlatformSpinner">
                  <Spinner type="button" theme="gray" />
                </span>
              ) : (
                <Icon name={platform.icon} weight="brand" size={16} />
              )}
              <span>{platform.label}</span>
            </button>
          )
        })}
      </div>

      {runtime.platforms.map((platform) => {
        const url = getPlatformFeedUrl(platform)
        return (
          <FeedCopyRow
            key={platform.id}
            label={platform.feedLabel}
            value={waitingForFirstFeed ? 'Фід ще не згенеровано' : url || 'Скоро буде доступно'}
            copyId={platform.id}
            copiedId={copiedId}
            disabled={waitingForFirstFeed || !url}
            onCopy={() => handleCopy(platform.id, url)}
          />
        )
      })}
    </div>
  )
}

export default XLSFeedCatalogPanel
